import { ActionTemplates, DejavooActions, buildBaseUri } from '@/config/dejavoo'
import { enrichDvPayLiteDataParam } from './dvPayLitePayload'

function generateRequestId() {
  return `req_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
}

function mergeParams(baseParams, overrides) {
  return Object.fromEntries(
    Object.entries({ ...(baseParams || {}), ...(overrides || {}) }).filter(
      ([, v]) => v !== undefined && v !== null && v !== ''
    )
  )
}

function openViaAnchor(url) {
  // Fallback approach to trigger external app from WebView
  const a = document.createElement('a')
  a.setAttribute('href', url)
  a.style.display = 'none'
  document.body.appendChild(a)
  a.click()
  setTimeout(() => {
    document.body.removeChild(a)
  }, 0)
}

function openViaLocation(url) {
  window.location.href = url
}

function openViaIframe(url) {
  const iframe = document.createElement('iframe')
  iframe.style.display = 'none'
  iframe.src = url
  document.body.appendChild(iframe)
  setTimeout(() => {
    document.body.removeChild(iframe)
  }, 1500)
}

function buildAndroidIntentUrl(deeplinkUrl, pkg) {
  try {
    const u = new URL(deeplinkUrl)
    const scheme = u.protocol.replace(':', '')
    const host = u.host
    const path = u.pathname || ''
    const query = u.search || ''
    const extras = [`scheme=${scheme}`]
    if (pkg) extras.push(`package=${pkg}`)
    return `intent://${host}${path}${query}#Intent;${extras.join(';')};end`
  } catch {
    return null
  }
}

function getAppLauncher() {
  // Access Capacitor AppLauncher via global to avoid bundling dependency
  const cap = typeof window !== 'undefined' ? window.Capacitor : undefined
  const plugins = cap?.Plugins || {}
  return plugins.AppLauncher
}

function getDvPayLauncher() {
  const cap = typeof window !== 'undefined' ? window.Capacitor : undefined
  const plugins = cap?.Plugins || {}

  return plugins.DvPayLauncher
}

function buildUri(action, overrides = {}, enrichOptions = {}) {
  const template = ActionTemplates[action]
  if (!template) {
    throw new Error(`Unknown Dejavoo action: ${action}`)
  }

  const requestId = generateRequestId()
  const params = mergeParams(template.params, overrides)
  if (typeof params.data === 'string') {
    params.data = enrichDvPayLiteDataParam(params.data, enrichOptions)
  }
  const allParams = { ...params }

  const url = buildBaseUri(template.path, allParams)
  return { url, requestId }
}

async function canOpen(url) {
  try {
    const plugin = getAppLauncher()
    if (plugin?.canOpenUrl) {
      const res = await plugin.canOpenUrl({ url })
      return { available: !!res?.value, method: 'AppLauncher.canOpenUrl', raw: res }
    }
    // Without plugin, we cannot reliably check; return unknown
    return { available: null, method: 'unknown' }
  } catch (err) {
    return { available: false, method: 'AppLauncher.canOpenUrl', error: err?.message || String(err) }
  }
}

async function launch(action, overrides = {}, enrichOptions = {}) {
  const { url, requestId } = buildUri(action, overrides, enrichOptions)

  try {
    const attempts = []
    const startedAt = Date.now()

    // Attempt 0: Native plugin with startActivityForResult to capture response
    try {
      const dvPlugin = getDvPayLauncher()
      if (dvPlugin?.openForResult) {
        const pluginResult = await dvPlugin.openForResult({ url })
        attempts.push({ method: 'DvPayLauncher.openForResult', result: pluginResult })

        console.debug('[DejavooDeepLink] DvPayLauncher.openForResult URL', url)

        // Log the plugin result for debugging
        let messages = pluginResult
        if (typeof messages === 'object') {
          messages = JSON.stringify(messages)
        }

        console.debug('[DejavooDeepLink] DvPayLauncher.openForResult PluginResult', messages)
        return {
          requestId,
          url,
          launched: true,
          method: 'DvPayLauncher.openForResult',
          attempts,
          pluginResult,
          startedAt,
          endedAt: Date.now()
        }
      } else {
        attempts.push({ method: 'DvPayLauncher.openForResult', skipped: true, reason: 'plugin not available' })
      }
    } catch (e0) {
      attempts.push({ method: 'DvPayLauncher.openForResult', error: e0?.message || String(e0) })
    }

    // Attempt 1: Capacitor AppLauncher if available
    try {
      const plugin = getAppLauncher()
      if (plugin?.openUrl) {
        const opened = await plugin.openUrl({ url })
        attempts.push({ method: 'AppLauncher.openUrl', result: opened })
        console.debug('[DejavooDeepLink] AppLauncher.openUrl', { url, opened })
        return {
          requestId,
          url,
          launched: true,
          method: 'AppLauncher.openUrl',
          attempts,
          startedAt,
          endedAt: Date.now()
        }
      } else {
        attempts.push({ method: 'AppLauncher.openUrl', skipped: true, reason: 'plugin not available' })
      }
    } catch (e1) {
      console.warn('[DejavooDeepLink] AppLauncher path failed', e1)
      attempts.push({ method: 'AppLauncher.openUrl', error: e1?.message || String(e1) })
    }

    // Attempt 1b: AppLauncher with intent:// fallback
    try {
      const plugin = getAppLauncher()
      if (plugin?.openUrl) {
        const intentUrl = buildAndroidIntentUrl(url)
        if (intentUrl) {
          const openedIntent = await plugin.openUrl({ url: intentUrl })
          attempts.push({ method: 'AppLauncher.openUrl(intent)', url: intentUrl, result: openedIntent })
          console.debug('[DejavooDeepLink] AppLauncher.openUrl(intent)', { intentUrl, openedIntent })
          return {
            requestId,
            url: intentUrl,
            launched: true,
            method: 'AppLauncher.openUrl(intent)',
            attempts,
            startedAt,
            endedAt: Date.now()
          }
        } else {
          attempts.push({ method: 'AppLauncher.openUrl(intent)', skipped: true, reason: 'failed to build intent url' })
        }
      }
    } catch (e1b) {
      attempts.push({ method: 'AppLauncher.openUrl(intent)', error: e1b?.message || String(e1b) })
    }

    // Attempt 2: window.location.href
    try {
      openViaLocation(url)
      console.debug('[DejavooDeepLink] window.location.href attempt', { url })
      return { requestId, url, launched: true, method: 'window.location', attempts, startedAt, endedAt: Date.now() }
    } catch (e2) {
      attempts.push({ method: 'window.location', error: e2?.message || String(e2) })
    }

    // Attempt 3: iframe
    try {
      openViaIframe(url)
      console.debug('[DejavooDeepLink] iframe attempt', { url })
      return { requestId, url, launched: true, method: 'iframe', attempts, startedAt, endedAt: Date.now() }
    } catch (e3) {
      attempts.push({ method: 'iframe', error: e3?.message || String(e3) })
    }

    // Attempt 4: anchor
    try {
      openViaAnchor(url)
      console.debug('[DejavooDeepLink] anchor attempt', { url })
      return { requestId, url, launched: true, method: 'anchor', attempts, startedAt, endedAt: Date.now() }
    } catch (e4) {
      attempts.push({ method: 'anchor', error: e4?.message || String(e4) })
    }

    // If none worked, report attempts
    return { requestId, url, launched: false, method: 'none', attempts, startedAt, endedAt: Date.now() }
  } catch (err) {
    return { requestId, url, launched: false, error: err?.message || String(err), method: 'exception' }
  }
}

export default {
  buildUri,
  canOpen,
  launch,
  DejavooActions
}
